import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {validateIssueBody} from '@github-ui/entity-validators'
import {clsx} from 'clsx'
import {useEffect, useMemo, useRef, useState, type RefObject} from 'react'

import {useUpdateDescriptionMutation} from '../../mutations/use-update-description-mutation'
import styles from './DescriptionEditor.module.css'
import {useLayoutBaseRepository, useLayoutPullRequestId, useLayoutPullRequestNumber} from '../../stores/selectors'

// eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- static string literal is safe
const PLACEHOLDER_TEXT = 'No description provided.' as SafeHTMLString

type DescriptionEditorProps = {
  /** Current description as sanitized HTML, or null/undefined if none. Used by the read-only viewer. */
  bodyHtml: SafeHTMLString | null | undefined
  /** Current description as raw markdown. Used as the initial draft when editing. */
  body: string
  isEditing: boolean
  onEditingChange: (isEditing: boolean) => void
  /** Class for the viewer container; ignored when editing. */
  viewerClassName?: string
  /** Element to receive focus when the editor is dismissed (e.g. the edit trigger). */
  returnFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Shared description display + editing experience for pull requests. Handles
 * toggling between a `NewMarkdownViewer` and a `CommentBox` editor, validation,
 * and saving via `useUpdateDescriptionMutation`.
 *
 * The parent owns the `isEditing` state so it can place the edit trigger
 * wherever it makes sense (e.g. an overview kebab menu, a panel title bar).
 */
export function DescriptionEditor({
  bodyHtml,
  body,
  isEditing,
  onEditingChange,
  viewerClassName,
  returnFocusRef,
}: DescriptionEditorProps) {
  const pullRequestNumber = useLayoutPullRequestNumber()
  const pullRequestId = useLayoutPullRequestId()
  const baseRepository = useLayoutBaseRepository()

  const currentBodyHtml = bodyHtml ?? ''
  const isEmptyDescription = currentBodyHtml === ''

  const [draft, setDraft] = useState(body)

  // Sync draft with the body prop when it changes externally (e.g. live update)
  // while the user is not actively editing. React 19 "set state during render" pattern.
  const [prevBody, setPrevBody] = useState(body)
  if (body !== prevBody) {
    setPrevBody(body)
    if (!isEditing) {
      setDraft(body)
    }
  }
  const [markdownErrorMessage, setMarkdownErrorMessage] = useState<string | undefined>(undefined)

  const editorRef = useRef<CommentBoxHandle>(null)
  useEffect(() => {
    if (isEditing) {
      editorRef.current?.focus()
    }
  }, [isEditing])

  // The trigger (e.g. edit button) usually re-mounts when leaving edit mode,
  // so defer focus until after the parent has re-rendered.
  const closeEditor = () => {
    onEditingChange(false)
    setMarkdownErrorMessage(undefined)
    if (returnFocusRef) {
      requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }

  const subject = useMemo<Subject>(
    () => ({
      type: 'pull_request',
      id: {id: pullRequestId.toString(), databaseId: pullRequestId},
      pullRequestNumber,
      repository: {
        databaseId: baseRepository.id,
        nwo: `${baseRepository.ownerLogin}/${baseRepository.name}`,
        slashCommandsEnabled: false,
      },
    }),
    [pullRequestId, pullRequestNumber, baseRepository],
  )

  const validationResult = useMemo(() => validateIssueBody(draft), [draft])

  const {mutateAsync: updateDescription, isPending: isSaving} = useUpdateDescriptionMutation(() => {
    setMarkdownErrorMessage('Failed to update the description.')
  })

  const handleSave = async () => {
    if (!validationResult.isValid) return
    try {
      await updateDescription({id: pullRequestId, body: draft})
      closeEditor()
    } catch {
      // Error already handled by onError callback in useUpdateDescriptionMutation
    }
  }

  const onCancel = () => {
    setDraft(body)
    closeEditor()
  }

  if (isEditing) {
    return (
      <div className={styles.editor}>
        <CommentBox
          ref={editorRef}
          subject={subject}
          value={draft}
          placeholder=""
          markdownErrorMessage={markdownErrorMessage}
          onChange={setDraft}
          onSave={handleSave}
          onCancel={onCancel}
          validationResult={validationResult}
          saveButtonText="Update"
          saveButtonTrailingIcon
          buttonSize="medium"
          disabled={isSaving}
        />
      </div>
    )
  }

  return (
    <NewMarkdownViewer
      verifiedHTML={isEmptyDescription ? PLACEHOLDER_TEXT : currentBodyHtml}
      markdownValue={body}
      className={clsx(viewerClassName, isEmptyDescription && 'fgColor-muted text-italic')}
    />
  )
}
